import React, { useMemo, useRef, useState } from 'react';
import HelpTip from '../../components/HelpTip';
import { CapDevicesModal, ClickableCapBadge } from '../../components/CapTypeBadges';
import { useDictText } from '../../components/DictSelect';
import { CapType } from '../../api/captype';
import {
    CapRecState,
    MaterialCapability,
    capRecState,
    saveMaterialCapabilities,
} from '../../api/material';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { alertDialog } from '../../utils/dialog';
import { useRowDragSort } from '../../utils/useRowDragSort';
import { useCan } from '../../store';

// 材料详情「适配工艺类型」卡片 (2026-10-28): 推荐 / 不推荐 / 未设置 三态 + 推荐项排序.
// 收起: 只展示推荐 (按排序) 与不推荐的徽标; 展开: 全部工艺类型成表, 逐项选状态, 推荐行拖拽 / 上移下移排序.
// 保存整体提交 (recommended 的顺序即 sno), 走审批.

type Draft = {
    rec: string[];
    not: string[];
    /** capKey → 该材料在此工艺类型下禁用的 deviceKey (默认都不禁用) */
    dis: Record<string, string[]>;
};

const STATE_OPTS: { value: CapRecState; label: string; on: string }[] = [
    { value: 'rec', label: '推荐', on: 'bg-emerald-500 text-white' },
    { value: 'not', label: '不推荐', on: 'bg-rose-500 text-white' },
    { value: 'none', label: '未设置', on: 'bg-slate-500 text-white' },
];

/** 当前已落库状态: 推荐按后端 sno 顺序, 只保留现存的工艺类型. */
function draftOf(rows: MaterialCapability[], known: Set<string>): Draft {
    const d: Draft = { rec: [], not: [], dis: {} };
    for (const r of rows) {
        if (!known.has(r.capKey)) continue;
        if (r.disabledDevices?.length) d.dis[r.capKey] = [...r.disabledDevices];
        const st = capRecState(r);
        if (st === 'rec') d.rec.push(r.capKey);
        else if (st === 'not') d.not.push(r.capKey);
    }
    return d;
}

/** 禁用设备的可比较串 (忽略顺序与空项). */
function disKey(dis: Record<string, string[]>): string {
    return Object.keys(dis)
        .filter((k) => dis[k].length > 0)
        .sort()
        .map((k) => `${k}:${[...dis[k]].sort().join(',')}`)
        .join('|');
}

export default function MaterialCapTypesCard({
    xcode,
    capability,
    capTypes,
    canUpdate,
    onOpenCap,
    onSaved,
}: {
    xcode: string;
    capability: MaterialCapability[];
    /** 全部工艺类型 (按 sort 升序) */
    capTypes: CapType[];
    canUpdate: boolean;
    onOpenCap: (cap: CapType) => void;
    onSaved: () => Promise<void>;
}) {
    const capByKey = useMemo(
        () => new Map(capTypes.map((c) => [c.capKey, c])),
        [capTypes],
    );
    const saved = useMemo(
        () => draftOf(capability, new Set(capByKey.keys())),
        [capability, capByKey],
    );
    const [expanded, setExpanded] = useState(false);
    const [draft, setDraft] = useState<Draft | null>(null);
    const [busy, setBusy] = useState(false);
    // 编辑态下的「具备设备」弹窗 (带禁用开关); 只读态仍走 onOpenCap 的普通弹窗
    const [devCap, setDevCap] = useState<CapType | null>(null);
    const canSeeDevices = useCan('device', 'get');
    const lightText = useDictText('light_source');
    const actionText = useDictText('cap_action');

    const cur = draft ?? saved;
    const dirty =
        !!draft &&
        (draft.rec.join() !== saved.rec.join() ||
            [...draft.not].sort().join() !== [...saved.not].sort().join() ||
            disKey(draft.dis) !== disKey(saved.dis));

    const stateOf = (capKey: string): CapRecState =>
        cur.rec.includes(capKey)
            ? 'rec'
            : cur.not.includes(capKey)
              ? 'not'
              : 'none';

    const setState = (capKey: string, st: CapRecState) => {
        const rec = cur.rec.filter((k) => k !== capKey);
        const not = cur.not.filter((k) => k !== capKey);
        if (st === 'rec') rec.push(capKey); // 新推荐排到末尾
        if (st === 'not') not.push(capKey);
        setDraft({ ...cur, rec, not });
    };

    const toggleDevice = (capKey: string, deviceKey: string, off: boolean) => {
        const list = (cur.dis[capKey] || []).filter((k) => k !== deviceKey);
        if (off) list.push(deviceKey);
        setDraft({ ...cur, dis: { ...cur.dis, [capKey]: list } });
    };

    const moveRec = (from: number, to: number) => {
        if (to < 0 || to >= cur.rec.length) return;
        const rec = [...cur.rec];
        const [k] = rec.splice(from, 1);
        rec.splice(to, 0, k);
        setDraft({ ...cur, rec });
    };

    const recBodyRef = useRef<HTMLTableSectionElement>(null);
    useRowDragSort(
        recBodyRef,
        'tr[data-sort-key]',
        (order) => setDraft({ ...cur, rec: order }),
        [expanded, canUpdate, cur.rec.join()],
    );

    const collapse = () => {
        setDraft(null);
        setExpanded(false);
    };

    const onSave = async () => {
        if (!draft) return;
        setBusy(true);
        try {
            const res = await saveMaterialCapabilities(
                xcode,
                draft.rec,
                draft.not,
                draft.dis,
            );
            // 走审批流时数据未变, onSaved 只是回读原值
            await onSaved();
            alertApprovalIfPending(res.message);
            collapse();
        } catch (e) {
            alertDialog(
                '保存失败: ' +
                    (e instanceof ApiError
                        ? `${e.code}: ${e.message}`
                        : String(e)),
            );
        } finally {
            setBusy(false);
        }
    };

    // 收起态: 推荐按排序, 不推荐按工艺类型 sort
    const recCaps = cur.rec
        .map((k) => capByKey.get(k))
        .filter((c): c is CapType => !!c);
    const notCaps = capTypes.filter((c) => cur.not.includes(c.capKey));

    // 展开态行序: 推荐 (按排序, 可拖) 在上; 其余固定按工艺类型 sort,
    // 点「不推荐 / 未设置」不挪行 (挪行会让下一次点击落到别的行上).
    const otherCaps = capTypes.filter((c) => !cur.rec.includes(c.capKey));

    const renderRow = (c: CapType, recIdx: number) => {
        const st = stateOf(c.capKey);
        const draggable = canUpdate && recIdx >= 0;
        const devN = (c.deviceKeys || []).length;
        return (
            <tr
                key={c.capKey}
                data-sort-key={draggable ? c.capKey : undefined}
                className={`bg-white ${draggable ? 'hover:bg-slate-50' : ''}`}
                style={draggable ? { cursor: 'grab' } : undefined}
                title={draggable ? '拖拽整行调整推荐顺序' : undefined}
            >
                <td className="td w-16 text-xs text-slate-400 whitespace-nowrap">
                    {recIdx >= 0 ? (
                        <>
                            {draggable && <span className="grip mr-1">⠿</span>}
                            {recIdx + 1}
                        </>
                    ) : (
                        '—'
                    )}
                </td>
                <td className="td">
                    <ClickableCapBadge cap={c} state={st} onOpen={onOpenCap} />
                </td>
                <td className="td text-xs text-slate-500 whitespace-nowrap">
                    {c.lightSource || c.action
                        ? `${lightText(c.lightSource)} · ${actionText(c.action)}`
                        : '—'}
                </td>
                <td className="td text-xs">
                    {canSeeDevices && devN > 0 ? (
                        <button
                            type="button"
                            className="text-brand-600 hover:underline"
                            title={`查看具备「${c.label}」的设备`}
                            onClick={() =>
                                canUpdate ? setDevCap(c) : onOpenCap(c)
                            }
                        >
                            {devN} 台
                        </button>
                    ) : (
                        <span className="text-slate-500">{devN} 台</span>
                    )}
                    {(cur.dis[c.capKey] || []).length > 0 && (
                        <span className="ml-1.5 text-rose-500">
                            禁用 {cur.dis[c.capKey].length}
                        </span>
                    )}
                </td>
                <td className="td">
                    {canUpdate ? (
                        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs">
                            {STATE_OPTS.map((o) => (
                                <button
                                    key={o.value}
                                    type="button"
                                    className={`px-2.5 py-1 ${
                                        st === o.value
                                            ? o.on
                                            : 'bg-white text-slate-500 hover:bg-slate-50'
                                    }`}
                                    onClick={() => setState(c.capKey, o.value)}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    ) : (
                        <span className="text-xs text-slate-500">
                            {STATE_OPTS.find((o) => o.value === st)?.label}
                        </span>
                    )}
                </td>
                <td className="td text-xs whitespace-nowrap">
                    {draggable && (
                        <span className="inline-flex gap-1.5">
                            <button
                                type="button"
                                className={moveBtnCls}
                                disabled={recIdx === 0}
                                onClick={() => moveRec(recIdx, recIdx - 1)}
                            >
                                上移
                            </button>
                            <button
                                type="button"
                                className={moveBtnCls}
                                disabled={recIdx === cur.rec.length - 1}
                                onClick={() => moveRec(recIdx, recIdx + 1)}
                            >
                                下移
                            </button>
                        </span>
                    )}
                </td>
            </tr>
        );
    };

    const moveBtnCls =
        'px-2.5 py-1 rounded border border-slate-200 bg-white text-xs text-slate-600 hover:border-brand-400 hover:text-brand-600 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-slate-600';

    return (
        <div className="card p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-semibold flex items-center gap-1.5">
                    适配工艺类型
                    <HelpTip text="推荐 / 不推荐 逐项设置, 推荐项可拖拽排序 (顺序即对外下发的 sno, 越靠前越优先)。不推荐不算适配, 下游按「推荐」判断可加工。点徽标查看具备该工艺的设备。" />
                </div>
                {capTypes.length > 0 && (
                    <button
                        type="button"
                        className="text-xs text-brand-600 hover:underline"
                        onClick={() =>
                            expanded ? collapse() : setExpanded(true)
                        }
                    >
                        {expanded
                            ? '收起 ▴'
                            : canUpdate
                              ? '展开设置 ▾'
                              : '展开全部 ▾'}
                    </button>
                )}
            </div>

            {capTypes.length === 0 ? (
                <span className="text-xs text-slate-300">
                    暂无工艺类型定义 (在「工艺类型管理」中维护)
                </span>
            ) : !expanded ? (
                <div className="space-y-2">
                    {[
                        { label: '推荐', caps: recCaps, st: 'rec' as const },
                        { label: '不推荐', caps: notCaps, st: 'not' as const },
                    ].map((g) => (
                        <div key={g.st} className="flex items-start gap-3">
                            <span className="text-xs text-slate-400 w-12 shrink-0 pt-0.5">
                                {g.label}
                            </span>
                            <div className="flex flex-wrap gap-2">
                                {g.caps.length === 0 ? (
                                    <span className="text-xs text-slate-300">
                                        —
                                    </span>
                                ) : (
                                    g.caps.map((c, i) => (
                                        <ClickableCapBadge
                                            key={c.capKey}
                                            cap={c}
                                            state={g.st}
                                            prefix={
                                                g.st === 'rec'
                                                    ? `${i + 1}.`
                                                    : undefined
                                            }
                                            onOpen={onOpenCap}
                                        />
                                    ))
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto border border-slate-100 rounded-lg">
                        <table className="w-full">
                            <thead className="bg-slate-50">
                                <tr>
                                    <th className="th">排序</th>
                                    <th className="th">工艺类型</th>
                                    <th className="th">光源 · 动作</th>
                                    <th className="th">具备设备</th>
                                    <th className="th">推荐状态</th>
                                    <th className="th">移动</th>
                                </tr>
                            </thead>
                            <tbody ref={recBodyRef}>
                                {recCaps.map((c, i) => renderRow(c, i))}
                            </tbody>
                            <tbody>
                                {otherCaps.map((c) => renderRow(c, -1))}
                            </tbody>
                        </table>
                    </div>
                    {canUpdate && (
                        <div className="flex items-center justify-end gap-2 mt-3">
                            <span className="text-xs text-slate-400 mr-auto">
                                推荐 {cur.rec.length} · 不推荐 {cur.not.length}
                                {dirty ? ' · 有未保存的修改' : ''}
                            </span>
                            <button
                                type="button"
                                className="btn-ghost"
                                disabled={busy}
                                onClick={collapse}
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                className="btn-primary disabled:opacity-50"
                                disabled={busy || !dirty}
                                onClick={onSave}
                            >
                                {busy ? '提交中…' : '保存'}
                            </button>
                        </div>
                    )}
                </>
            )}
            {devCap && (
                <CapDevicesModal
                    cap={devCap}
                    onClose={() => setDevCap(null)}
                    disabledKeys={cur.dis[devCap.capKey] || []}
                    onToggleDisabled={(dk, off) =>
                        toggleDevice(devCap.capKey, dk, off)
                    }
                    extraFooter={
                        <div className="text-xs text-slate-400 mt-3">
                            禁用仅对当前材料在此工艺类型下生效, 关闭弹窗后点「保存」提交。
                        </div>
                    }
                />
            )}
        </div>
    );
}
